import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link as RouterLink, useNavigate, useLocation } from 'react-router-dom';
import { useMember } from '../contexts/MemberContext';
import { createCheckoutSession, getMonthlyPriceId, getAnnualPriceId, isStripeConfigured } from '../utils/stripe';
import { createMemberProfile } from '../utils/supabase';

const Signup = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [currentStep, setCurrentStep] = useState(1);
  const [selectedPlan] = useState('freedom');
  const [billingPeriod, setBillingPeriod] = useState(location.state?.billingPeriod || 'monthly');
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { startMembership } = useMember();
  const [agreedWaiver, setAgreedWaiver] = useState(false);
  const [agreedTerms, setAgreedTerms] = useState(false);
  const [formData, setFormData] = useState({
    // Personal Details
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
    phone: '',
    dateOfBirth: '',
    // Health Information
    allergies: [],
    healthGoals: [],
    fitnessLevel: '',
  });



  const plans = [
    {
      id: 'freedom',
      name: 'Freedom',
      price: 25,
      period: 'per month',
      features: [
        'Full recipe library with filters (allergy-friendly, diet type)',
        'Monthly meal plan (4 weeks + shopping list)',
        'Complete workout library (glute, corporate, prenatal, etc)',
        'Exclusive wellness guides (eczema, anti-inflammatory)',
        'Premium community access (Q&As, recipe swaps)',
        'Early access to new products + free seasonal ebooks',
        'Quarterly Zoom workshops (skin health, fitness, holistic living)'
      ],
      popular: true
    }
  ];

  const allergyOptions = [
    'Gluten', 'Dairy', 'Tree Nuts', 'Peanuts', 'Wheat', 'Sugar', 'Soy', 'Eggs', 'Shellfish', 'Fish', 'Sesame', 'Other'
  ];

  const healthGoalOptions = [
    'Weight Management', 'Digestive Health', 'Energy Boost', 'Skin Health', 
    'Stress Reduction', 'Better Sleep', 'Hormone Balance', 'Immune Support'
  ];

  const fitnessLevels = [
    { id: 'beginner', label: 'Beginner', description: 'New to fitness or returning after a break' },
    { id: 'intermediate', label: 'Intermediate', description: 'Regular exercise 2-3 times per week' },
    { id: 'advanced', label: 'Advanced', description: 'Consistent training 4+ times per week' }
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name.includes('.')) {
      const [parent, child] = name.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleArrayToggle = (array, item) => {
    setFormData(prev => ({
      ...prev,
      [array]: prev[array].includes(item)
        ? prev[array].filter(i => i !== item)
        : [...prev[array], item]
    }));
  };

  const validateCurrentStep = () => {
    switch (currentStep) {
      case 1:
        return (
          formData.firstName && formData.lastName && formData.email
        );
      case 2:
        return (
          formData.fitnessLevel !== '' &&
          agreedWaiver && agreedTerms
        );
      default:
        return true;
    }
  };

  const handleNextStep = (e) => {
    if (e) e.preventDefault();
    console.log('handleNextStep called, current step:', currentStep);
    
    if (currentStep >= 2) {
      console.log('Already on final step');
      return;
    }
    
    if (validateCurrentStep()) {
      console.log('Moving to next step');
      setCurrentStep(currentStep + 1);
      window.scrollTo(0, 0);
    } else {
      alert('Please fill in all required fields before proceeding.');
    }
  };

  const handlePrevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
      window.scrollTo(0, 0);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    console.log('=== HANDLE SUBMIT CALLED ===');
    console.log('Current Step:', currentStep);
    console.log('Is Submitting:', isSubmitting);
    
    // Prevent multiple rapid submits
    if (isSubmitting) {
      console.log('Already submitting, ignoring');
      return;
    }
    
    // Only proceed if we're on step 2 (the final step)
    if (currentStep !== 2) {
      console.log('Not on final step, ignoring submit');
      return;
    }
    
    // Validate step 2
    if (!validateCurrentStep()) {
      alert('Please select your fitness level and check both agreement boxes before continuing to payment.');
      setIsLoading(false);
      return;
    }
    
    setIsSubmitting(true);
    setIsLoading(true);
    
    try {
      // Check if Stripe is configured
      const stripeConfigured = isStripeConfigured();
      console.log('Is Stripe Configured?', stripeConfigured);
      
      if (!stripeConfigured) {
        console.error('Stripe not configured. Keys:', {
          hasPublishableKey: !!process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY,
          hasPaymentLink: !!process.env.REACT_APP_STRIPE_PAYMENT_LINK
        });
        alert('Payment system is not configured. Please contact support.');
        setIsLoading(false);
        return;
      }
      
      console.log('Stripe configured, proceeding with checkout...');

      // Save user data to localStorage before redirecting to Stripe
      const fullName = [formData.firstName, formData.lastName].filter(Boolean).join(' ').trim();
      const displayName = fullName || formData.firstName || '';
      
      try {
        // Always save email for data sync — this is critical
        localStorage.setItem('userEmail', formData.email);
        if (displayName) {
          localStorage.setItem('memberProfileName', displayName);
        }
        // Save signup data temporarily; will be written to Supabase on success page
        const signupData = {
          name: displayName,
          email: formData.email,
          firstName: formData.firstName,
          lastName: formData.lastName,
          allergies: formData.allergies,
          healthGoals: formData.healthGoals,
          fitnessLevel: formData.fitnessLevel,
          waiverAccepted: agreedWaiver,
          termsAccepted: agreedTerms
        };
        localStorage.setItem('pendingSignupData', JSON.stringify(signupData));
        console.log('Deferred member profile creation to success page.');
      } catch (err) {
        console.error('Error saving signup data:', err);
        // Continue anyway - don't block payment flow
      }

      // Get the correct price ID based on billing period
      const priceId = billingPeriod === 'annual' ? getAnnualPriceId() : getMonthlyPriceId();
      console.log('Using price ID:', priceId, 'for billing period:', billingPeriod);
      
      // Redirect to Stripe Checkout with all signup data
      await createCheckoutSession(priceId, formData.email, {
        firstName: formData.firstName,
        lastName: formData.lastName,
        allergies: formData.allergies,
        healthGoals: formData.healthGoals,
        fitnessLevel: formData.fitnessLevel
      });
      
    } catch (error) {
      console.error('Subscription error:', error);
      alert('There was an error processing your subscription. Please try again.');
      setIsLoading(false);
      setIsSubmitting(false);
    }
  };

  const renderStepIndicator = () => (
    <div className="flex items-center justify-center mb-8">
      {[1, 2].map((step) => (
        <div key={step} className="flex items-center">
          <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium`}
            style={{ 
              backgroundColor: step <= currentStep ? '#d9a79c' : '#f0e0dc',
              color: step <= currentStep ? 'white' : '#999'
            }}>
            {step}
          </div>
          {step < 2 && (
            <div className="w-12 h-0.5 mx-2"
              style={{ backgroundColor: step < currentStep ? '#d9a79c' : '#f0e0dc' }} />
          )}
        </div>
      ))}
    </div>
  );

  // Plan selection removed: single Freedom plan is default

  const renderPersonalDetails = () => (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="space-y-6"
    >
      <div className="text-center mb-8">
        <h2 className="text-2xl font-semibold text-charcoal-900 mb-2">Personal Details</h2>
        <p className="text-charcoal-600">Tell us about yourself to personalize your experience</p>
      </div>

      {/* Billing Period Selection */}
      <div className="bg-gradient-to-br from-rose-50/40 to-beige-50/40 rounded-2xl p-6 border-2" style={{ borderColor: '#f0e0dc' }}>
        <h3 className="text-lg font-semibold text-charcoal-900 mb-4 text-center">Choose Your Billing Period</h3>
        
        {/* Toggle Buttons */}
        <div className="flex items-center justify-center gap-3 mb-4">
          <button
            type="button"
            onClick={() => setBillingPeriod('monthly')}
            className={`px-6 py-3 rounded-full font-medium transition-all duration-300 ${
              billingPeriod === 'monthly'
                ? 'text-white shadow-soft'
                : 'text-charcoal-600 bg-white/50 hover:bg-white'
            }`}
            style={billingPeriod === 'monthly' ? { backgroundColor: '#d9a79c' } : {}}
          >
            Monthly
          </button>
          <button
            type="button"
            onClick={() => setBillingPeriod('annual')}
            className={`px-6 py-3 rounded-full font-medium transition-all duration-300 ${
              billingPeriod === 'annual'
                ? 'text-white shadow-soft'
                : 'text-charcoal-600 bg-white/50 hover:bg-white'
            }`}
            style={billingPeriod === 'annual' ? { backgroundColor: '#d9a79c' } : {}}
          >
            Annual
          </button>
        </div>

        {/* Price Display */}
        <div className="text-center">
          <div className="text-3xl font-bold text-charcoal-900 mb-1">
            {billingPeriod === 'monthly' ? '$25' : '$275'}
            <span className="text-lg font-normal text-charcoal-600 ml-2">
              /{billingPeriod === 'monthly' ? 'month' : 'year'}
            </span>
          </div>
          {billingPeriod === 'annual' && (
            <p className="text-sm text-green-600 font-medium">
              $22.92/month when billed annually • Save $25 (1 month free!)
            </p>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-charcoal-700 mb-2">
            First Name <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            name="firstName"
            value={formData.firstName}
            onChange={handleInputChange}
            style={{ borderColor: '#f0e0dc' }}
            className="w-full px-4 py-3 border rounded-2xl focus:outline-none focus:ring-2"
            onFocus={(e) => e.currentTarget.style.borderColor = '#d9a79c'}
            onBlur={(e) => e.currentTarget.style.borderColor = '#f0e0dc'}
            required
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-charcoal-700 mb-2">
            Last Name <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            name="lastName"
            value={formData.lastName}
            onChange={handleInputChange}
            style={{ borderColor: '#f0e0dc' }}
            className="w-full px-4 py-3 border rounded-2xl focus:outline-none focus:ring-2"
            onFocus={(e) => e.currentTarget.style.borderColor = '#d9a79c'}
            onBlur={(e) => e.currentTarget.style.borderColor = '#f0e0dc'}
            required
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-charcoal-700 mb-2">
          Email Address <span className="text-red-500">*</span>
        </label>
        <input
          type="email"
          name="email"
          value={formData.email}
          onChange={handleInputChange}
          className="w-full px-4 py-3 border border-sage-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-sage-500"
          required
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-charcoal-700 mb-2">Phone Number (Optional)</label>
        <input
          type="tel"
          name="phone"
          value={formData.phone}
          onChange={handleInputChange}
          className="w-full px-4 py-3 border border-sage-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-sage-500"
        />
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
        <p className="text-sm text-blue-800">
          💡 After payment, we'll send you an email with a link to set your password and access your account.
        </p>
      </div>
    </motion.div>
  );

  const renderHealthProfile = () => (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="space-y-6"
    >
      <div className="text-center mb-8">
        <h2 className="text-2xl font-semibold text-charcoal-900 mb-2">Health Profile</h2>
        <p className="text-charcoal-600">Help us customize your wellness experience</p>
      </div>

      <div>
        <label className="block text-sm font-medium text-charcoal-700 mb-3">
          Food Allergies & Sensitivities <span className="text-charcoal-500">(Optional)</span>
        </label>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {allergyOptions.map((allergy) => (
            <button
              key={allergy}
              type="button"
              onClick={() => handleArrayToggle('allergies', allergy)}
              className="p-3 rounded-xl border-2 text-sm font-medium transition-all duration-200"
              style={{
                borderColor: formData.allergies.includes(allergy) ? '#d9a79c' : '#f0e0dc',
                backgroundColor: formData.allergies.includes(allergy) ? '#fef2f2' : 'white',
                color: formData.allergies.includes(allergy) ? '#d9a79c' : '#4a5568'
              }}
            >
              {allergy}
            </button>
          ))}
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-charcoal-700 mb-3">
          Health Goals <span className="text-charcoal-500">(Optional)</span>
        </label>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {healthGoalOptions.map((goal) => (
            <button
              key={goal}
              type="button"
              onClick={() => handleArrayToggle('healthGoals', goal)}
              className="p-3 rounded-xl border-2 text-sm font-medium transition-all duration-200"
              style={{
                borderColor: formData.healthGoals.includes(goal) ? '#d9a79c' : '#f0e0dc',
                backgroundColor: formData.healthGoals.includes(goal) ? '#fef2f2' : 'white',
                color: formData.healthGoals.includes(goal) ? '#d9a79c' : '#4a5568'
              }}
            >
              {goal}
            </button>
          ))}
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-charcoal-700 mb-3">
          Fitness Level <span className="text-red-500">*</span>
        </label>
        <div className="space-y-3">
          {fitnessLevels.map((level) => (
            <button
              key={level.id}
              type="button"
              onClick={() => setFormData(prev => ({ ...prev, fitnessLevel: level.id }))}
              className="w-full p-4 rounded-xl border-2 text-left transition-all duration-200"
              style={{
                borderColor: formData.fitnessLevel === level.id ? '#d9a79c' : '#f0e0dc',
                backgroundColor: formData.fitnessLevel === level.id ? '#fef2f2' : 'white'
              }}
            >
              <div className="font-medium text-charcoal-900">{level.label}</div>
              <div className="text-sm text-charcoal-600">{level.description}</div>
            </button>
          ))}
        </div>
      </div>

      {/* Waiver and Terms */}
      <div className="mt-8 pt-6 border-t" style={{ borderColor: '#f0e0dc' }}>
        <div className="bg-rose-50 p-6 rounded-2xl mb-4">
          <h3 className="font-semibold text-charcoal-900 mb-3">Waiver of Liability & Community Agreement</h3>
          <div className="text-sm text-charcoal-700 space-y-2 max-h-40 overflow-y-auto">
            <p>
              <span className="font-semibold">Assumption of Risk.</span> I understand that participation in fitness activities, meal planning, and wellness programs carries inherent risks, including but not limited to physical injury, illness, or exacerbation of pre-existing conditions.
            </p>
            <p>
              <span className="font-semibold">Release of Liability.</span> I voluntarily assume all risks associated with my participation and release Alera, its owners, employees, and affiliates from any liability for injuries or damages arising from my use of the platform or participation in any activities.
            </p>
            <p>
              <span className="font-semibold">Community Guidelines.</span> I agree to adhere to the Alera Community Guidelines, which require all members to maintain a positive, safe, respectful, and welcoming environment.
            </p>
          </div>
        </div>
        <div className="space-y-3">
          <label className="flex items-start space-x-3">
            <input type="checkbox" style={{ accentColor: '#d9a79c' }} className="mt-1 h-4 w-4" checked={agreedWaiver} onChange={(e) => setAgreedWaiver(e.target.checked)} />
            <span className="text-sm text-charcoal-800">I have read and agree to the Waiver of Liability & Community Agreement above. <span className="text-red-500">*</span></span>
          </label>
          <label className="flex items-start space-x-3">
            <input type="checkbox" style={{ accentColor: '#d9a79c' }} className="mt-1 h-4 w-4" checked={agreedTerms} onChange={(e) => setAgreedTerms(e.target.checked)} />
            <span className="text-sm text-charcoal-800">I agree to the <RouterLink to="/terms-conditions" style={{ color: '#d9a79c' }} className="underline hover:opacity-80">Terms & Conditions</RouterLink>. <span className="text-red-500">*</span></span>
          </label>
        </div>
      </div>
    </motion.div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-50 via-beige-50 to-rose-100 pt-24 pb-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-2xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-8"
        >
          <h1 className="text-3xl font-playfair font-semibold text-charcoal-900 mb-2">
            Join the Alera Community
          </h1>
          <p className="text-charcoal-600">Start your personalized wellness journey today</p>
        </motion.div>

        <div className="bg-white/80 backdrop-blur-sm rounded-3xl shadow-soft p-8">
          {renderStepIndicator()}

          <form onSubmit={handleSubmit} onKeyDown={(e) => {
            // Prevent Enter key from submitting form unless on step 2 with submit button
            if (e.key === 'Enter') {
              console.log('Enter pressed, current step:', currentStep, 'target type:', e.target.type);
              if (currentStep === 1) {
                e.preventDefault();
                handleNextStep(e);
              } else if (currentStep === 2 && e.target.type !== 'submit') {
                // Prevent Enter on step 2 unless it's the submit button
                e.preventDefault();
              }
            }
          }}>
            <AnimatePresence mode="wait">
              {currentStep === 1 && renderPersonalDetails()}
              {currentStep === 2 && renderHealthProfile()}
            </AnimatePresence>

            <div className="flex justify-between mt-8 pt-6 border-t" style={{ borderColor: '#f0e0dc' }}>
              {currentStep > 1 && (
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  type="button"
                  onClick={handlePrevStep}
                  style={{ borderColor: '#d9a79c', color: '#d9a79c' }}
                  className="px-6 py-3 border rounded-2xl font-medium hover:bg-rose-50 transition-colors duration-200"
                >
                  Previous
                </motion.button>
              )}

              {currentStep < 2 ? (
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  type="button"
                  onClick={handleNextStep}
                  style={{ backgroundColor: '#d9a79c' }}
                  className="ml-auto px-6 py-3 text-white rounded-2xl font-medium hover:opacity-90 transition-all duration-200 shadow-soft"
                >
                  Next Step
                </motion.button>
              ) : (
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  type="submit"
                  disabled={isLoading}
                  style={{ backgroundColor: '#d9a79c' }}
                  className="ml-auto px-8 py-3 text-white rounded-2xl font-medium hover:opacity-90 hover:shadow-soft-lg transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoading ? (
                    <div className="flex items-center">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Redirecting to Payment...
                    </div>
                  ) : (
                    'Continue to Payment'
                  )}
                </motion.button>
              )}
            </div>
          </form>
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="text-center mt-6"
        >
          <p className="text-sm text-charcoal-600 mb-2">
            Already have an account?{' '}
            <RouterLink to="/login" className="text-sage-600 hover:text-sage-700 font-medium">
              Sign in here
            </RouterLink>
          </p>
          <RouterLink
            to="/"
            className="text-sm text-charcoal-500 hover:text-charcoal-700 transition-colors duration-200"
          >
            ← Back to Home
          </RouterLink>
        </motion.div>
      </div>
    </div>
  );
};

export default Signup;
